import React from 'react';

interface BarData {
  label: string;
  value2025: number;
  value2026: number;
  value2027: number;
}

export default function ProjectedComputeEndUserBreakdown() {
  // Chart dimensions
  const width = 850;
  const height =  900; // Taller to accommodate more rows
  const margin = { top: 60, right: 40, bottom: 60, left: 180 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;
  
  // Data for the bars in H100e (millions)
  const data: BarData[] = [
    { 
      label: "OpenAI", 
      value2025: 2,
      value2026: 6,
      value2027: 20
    },
    { 
      label: "Anthropic", 
      value2025: 1.3,
      value2026: 4,
      value2027: 14
    },
    { 
      label: "Google AGI\ndevelopment", 
      value2025: 2.2,
      value2026: 5.6,
      value2027: 16
    },
    { 
      label: "Rest of Google", 
      value2025: 1.8,
      value2026: 1.9,
      value2027: 2
    },
    { 
      label: "xAI", 
      value2025: 1.5,
      value2026: 4,
      value2027: 9
    },
    { 
      label: "Meta AGI\ndevelopment", 
      value2025: 1.3,
      value2026: 3.2,
      value2027: 7
    },
    { 
      label: "Rest of Meta", 
      value2025: 1.3,
      value2026: 1.4,
      value2027: 2
    },
    { 
      label: "Rest of the US", 
      value2025: 3.6,
      value2026: 5.2,
      value2027: 10
    },
    { 
      label: "China AGI\ndevelopment", 
      value2025: 0.404,
      value2026: 2.2,
      value2027: 10
    },
    { 
      label: "Rest of China", 
      value2025: 1.6,
      value2026: 1.8,
      value2027: 2
    },
    { 
      label: "Rest of the world", 
      value2025: 2.6,
      value2026: 4.6,
      value2027: 9
    }
  ];
  
  // Find maximum value for scaling
  const maxValue = Math.max(
    ...data.map(d => Math.max(d.value2025, d.value2026, d.value2027))
  );
  
  // Round up to nearest 5M for nice axis
  const axisMax = 20; // Fixed to 20M as in the image
  
  // X-axis ticks
  const xTicks = [0, 5, 10, 15, 20];
  
  // Bar height and spacing
  const barHeight = 15;
  const barSpacing = chartHeight / data.length;
  
  // Scale for x-axis
  const xScale = (value: number) => (value / axisMax) * chartWidth;
  
  // Format values with M for millions or K for thousands
  const formatValue = (value: number) => {
    if (value < 1) {
      return `${(value * 1000).toFixed(0)}K`;
    }
    return `${value.toFixed(1)}M`;
  };
  
  return (
    <div className="w-full relative overflow-hidden mt-8 mb-8">
      <svg
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Chart Title */}
        <text
          x={margin.left}
          y={30}
          fontSize="22"
          fontFamily="monospace"
          className="text-[22px] font-mono md:text-[20px] sm:text-[18px]"
          fill="#666666"
        >
          Projected breakdown by compute end-user
        </text>
        
        {/* Subtitle */}
        <text
          x={margin.left}
          y={55}
          fontSize="16"
          fontFamily="monospace"
          className="text-[16px] font-mono italic md:text-[14px] sm:text-[12px]"
          fill="#888888"
          fontStyle="italic"
        >
          Projection, in H100e
        </text>
        
        {/* Vertical grid lines */}
        {xTicks.map((tick) => (
          <g key={`tick-${tick}`}>
            <line
              x1={margin.left + xScale(tick)}
              y1={margin.top}
              x2={margin.left + xScale(tick)}
              y2={margin.top + chartHeight}
              stroke="#e0e0e0"
              strokeWidth="1"
            />
            <text
              x={margin.left + xScale(tick)}
              y={margin.top + chartHeight + 20}
              textAnchor="middle"
              fontSize={14}
              fontFamily="monospace"
              fill="#666666"
            >
              {tick}M
            </text>
          </g>
        ))}
        
        {/* Y-axis labels (Company names) */}
        {data.map((d, i) => {
          // Split multi-line labels
          const lines = d.label.split('\n');
          
          return (
            <g key={`label-${i}`}>
              {lines.map((line, lineIndex) => (
                <text
                  key={`label-${i}-line-${lineIndex}`}
                  x={margin.left - 10}
                  y={margin.top + i * barSpacing + barSpacing / 2 + (lineIndex - lines.length / 2 + 0.5) * 18}
                  textAnchor="end"
                  fontSize={14}
                  fontFamily="monospace"
                  fontWeight="bold"
                  fill="#000000"
                >
                  {line}
                </text>
              ))}
            </g>
          );
        })}
        
        {/* 2025 Bars (Blue) */}
        {data.map((d, i) => (
          <g key={`bar2025-${i}`}>
            <rect
              x={margin.left}
              y={margin.top + i * barSpacing + (barSpacing - barHeight * 3 - 10) / 2}
              width={xScale(d.value2025)}
              height={barHeight}
              fill="#333333"
              rx={2}
              ry={2}
            />
            <text
              x={margin.left + xScale(d.value2025) + 5}
              y={margin.top + i * barSpacing + (barSpacing - barHeight * 3 - 10) / 2 + barHeight / 2 + 4}
              textAnchor="start"
              fontSize={14}
              fontFamily="monospace"
              fill="#333333"
            >
              {formatValue(d.value2025)}
            </text>
          </g>
        ))}
        
        {/* 2026 Bars (Red) */}
        {data.map((d, i) => (
          <g key={`bar2026-${i}`}>
            <rect
              x={margin.left}
              y={margin.top + i * barSpacing + (barSpacing - barHeight * 3 - 10) / 2 + barHeight + 5}
              width={xScale(d.value2026)}
              height={barHeight}
              fill="var(--accent, #006000)"
              rx={2}
              ry={2}
            />
            <text
              x={margin.left + xScale(d.value2026) + 5}
              y={margin.top + i * barSpacing + (barSpacing - barHeight * 3 - 10) / 2 + (barHeight + 5) + barHeight/2+4}
              textAnchor="start"
              fontSize={14}
              fontFamily="monospace"
              fill="#333333"
            >
              {formatValue(d.value2026)}
            </text>
          </g>
        ))}
        
        {/* 2027 Bars (Yellow) */}
        {data.map((d, i) => (
          <g key={`bar2027-${i}`}>
            <rect
              x={margin.left}
              y={margin.top + i * barSpacing + (barSpacing - barHeight * 3 - 10) / 2 + (barHeight + 5) * 2}
              width={xScale(d.value2027)}
              height={barHeight}
              fill="rgb(127, 43, 43)"
              rx={2}
              ry={2}
            />
            <text
              x={margin.left + xScale(d.value2027) + 5}
              y={margin.top + i * barSpacing + (barSpacing - barHeight * 3 - 10) / 2 + (barHeight + 5) * 2 + barHeight / 2 + 4}
              textAnchor="start"
              fontSize={14}
              fontFamily="monospace"
              fill="#333333"
            >
              {d.value2027 > 0 ? `${d.value2027}M` : ''}
            </text>
          </g>
        ))}
        
        {/* Legend */}
        <g transform={`translate(${margin.left}, ${margin.top + chartHeight + 40})`}>
          {/* 2025 Legend Item */}
          <rect
            x={0}
            y={0}
            width={15}
            height={15}
            fill="#333333"
            rx={2}
            ry={2}
          />
          <text
            x={20}
            y={12}
            fontSize={14}
            fontFamily="monospace"
            fill="#333333"
          >
            December 2025
          </text>
          
          {/* 2026 Legend Item */}
          <rect
            x={160}
            y={0}
            width={15}
            height={15}
            fill="#006000"
            rx={2}
            ry={2}
          />
          <text
            x={180}
            y={12}
            fontSize={14}
            fontFamily="monospace"
            fill="#333333"
          >
            December 2026
          </text>
          
          {/* 2027 Legend Item */}
          <rect
            x={320}
            y={0}
            width={15}
            height={15}
            fill="rgb(127, 43, 43)"
            rx={2}
            ry={2}
          />
          <text
            x={340}
            y={12}
            fontSize={14}
            fontFamily="monospace"
            fill="#333333"
          >
            December 2027
          </text>
        </g>
      </svg>
    </div>
  );
}